import { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

function apiUrl(path) {
  const configured = (process.env.REACT_APP_API_URL || '/api').replace(/\/+$/, '');
  const base = /^https?:\/\/[^/]+$/i.test(configured)
    ? `${configured}/api`
    : configured;
  return `${base}/${String(path).replace(/^\/+/, '')}`;
}

export default function useOnboardingStatus() {
  const {
    isAuthenticated,
    isLoading: authLoading,
    user,
    getAccessTokenSilently,
  } = useAuth0();
  const [state, setState] = useState({
    status: 'idle',
    subscription: null,
    error: null,
  });

  const refresh = useCallback(async () => {
    if (authLoading) return null;
    if (!isAuthenticated || !user?.email_verified) {
      setState({ status: 'idle', subscription: null, error: null });
      return null;
    }

    setState((current) => ({ ...current, status: 'loading', error: null }));
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        },
      });
      const response = await fetch(apiUrl('/user/subscription'), {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });

      if (response.status === 404) {
        setState({ status: 'needs_plan', subscription: null, error: null });
        return null;
      }

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'onboarding_status_failed');
      }

      if (payload.paymentReviewRequired) throw new Error('payment_review_required');
      const status = payload.eligible ? 'eligible' : 'needs_plan';
      setState({ status, subscription: payload, error: null });
      return payload;
    } catch (error) {
      setState({
        status: 'error',
        subscription: null,
        error: error?.message === 'account_blocked'
          ? 'Access to this account is paused. Contact Virgil support for help.'
          : error?.message === 'payment_review_required'
            ? 'Your payment needs a review. Contact Virgil support before trying another checkout.'
            : 'We could not check your beta access. Please try again.',
      });
      return null;
    }
  }, [
    authLoading,
    getAccessTokenSilently,
    isAuthenticated,
    user?.email_verified,
  ]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { ...state, refresh };
}

export { apiUrl };
